<template>
    <div>
        <Teleport to="body">
            <div class="modal-wrapper">
                <Transition name="dialog"
                            appear>
                    <div class="modal modal-transition"
                         :class="{ 'newsletter': subscribeModalStore.isNewsletter }"
                         @click="subscribeModalStore.setSubscribeModal(false)">
                        <div class="modal-wrapper">
                            <div class="modal-content"
                                 @click.stop>
                                <form @submit.prevent="loadRecaptchaAndProceed">
                                    <div class="close-button-wrapper">
                                        <span class="close-button"
                                              @click="subscribeModalStore.setSubscribeModal(false)">&times;</span>
                                    </div>
                                    <div class="subscribe-form-wrapper">
                                        <div class="subscribe-image-wrapper">
                                            <img class="subscribe-image"
                                                 loading="lazy"
                                                 :src="asset_cdn('assets/subscribe.svg')"
                                                 :alt="getTrans('quests-dashboard.subscribe', 'Subscribe')"
                                                 :title="getTrans('quests-dashboard.subscribe', 'Subscribe')">
                                        </div>
                                        <div>
                                            <div class="subscribe-title-wrapper">
                                                <h2 class="subscribe-title">
                                                    <template v-if="title && subscribeModalStore.isNewsletter">
                                                        <!-- eslint-disable vue/no-v-html -->
                                                        <span v-html="title" />
                                                    <!-- eslint-enable -->
                                                    </template>
                                                    <Translate v-else
                                                               translationKey="subscribe.title"
                                                               fallback="Subscribe to the Early Bird List!"
                                                               :hasHtmlTags="true" />
                                                </h2>
                                            </div>
                                            <div class="subscribe-subtitle modal-subtitle">
                                                <template v-if="subtitle && subscribeModalStore.isNewsletter">
                                                    {{ subtitle }}
                                                </template>
                                                <Translate v-else
                                                           translationKey="subscribe.subtitle"
                                                           fallback="Be the first in row to get this feature and the latest updates."
                                                           :hasHtmlTags="true" />
                                            </div>
                                            <div class="content">
                                                <div class="form-group">
                                                    <input v-model="email"
                                                           type="email"
                                                           :placeholder="getTrans('subscribe.inputPlaceholder', 'Enter email here')"
                                                           :class="{ 'error-wrapper--active': errorMessage }"
                                                           class="input form-control email"
                                                           name="email"
                                                           required>
                                                    <div v-if="errorMessage"
                                                         class="error-message">
                                                        {{ errorMessage }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div class="subscribe-button-wrapper">
                                            <div id="subscribe-modal-proceed-button" />
                                            <BaseButton data-callback="onSubscribeSubmit"
                                                        type="submit">
                                                <template v-if="buttonTitle && subscribeModalStore.isNewsletter">
                                                    {{ buttonTitle }}
                                                </template>
                                                <Translate v-else
                                                           translation-key="quests-dashboard.subscribe"
                                                           fallback="Subscribe" />
                                            </BaseButton>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { useSubscribeModalStore } from '@/js/store/subscribe/SubscribeModalStore';
    import { asset_cdn } from '@/js/helpers/helpers';
    import { getTrans } from '@/js/helpers/getTrans';
    import { subscriberSourceEnum } from '@/js/enums/subscriberSourceEnum';
    import { useSubscribeForm } from '../composables/useSubscribeForm';
    import { useModalActive } from '@/js/composables/useModalActive';
    import BaseButton from '@/js/components/BaseButton.vue';
    import { useModalEscape } from '@/js/composables/useModalEscape';

    interface IProps {
        title?: string,
        subtitle?: string,
        buttonTitle?: string,
    }

    defineProps<IProps>();

    const subscribeModalStore = useSubscribeModalStore();

    const SUBSCRIBE_FORM_BUTTON_ID = 'subscribe-modal-proceed-button';
    const computedSubscriberSource = computed(() => ([subscriberSourceEnum.SOURCE_NEWSLETTER, subscriberSourceEnum.SOURCE_NEWSLETTER_POPUP]));

    const { email, errorMessage, loadRecaptchaAndProceed } = useSubscribeForm(subscriberSourceEnum.SOURCE_NEWSLETTER_POPUP, computedSubscriberSource.value, SUBSCRIBE_FORM_BUTTON_ID);

    useModalEscape(() => subscribeModalStore.setSubscribeModal(false));
    useModalActive();
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/design-system/variables" as variables;
@use "@/sass/partials/breakpoints" as breakpoints;
@use "@/sass/partials/z-index" as ZIndex;
@use "@/sass/design-system/fonts" as fonts;

.modal-transition {
    display: flex;
    align-items: center;
    justify-content: center;

    &.modal {
        z-index: ZIndex.$modal-transition-max-z-index;
    }

    .subscribe-title {
        margin-top: 24px;
        margin-bottom: 8px;
        font-size: 32px;
        text-align: center;
    }

    &.newsletter {
        .subscribe-title {
            font-size: 38px;
        }
    }

    .modal-wrapper {
        max-width: 600px;

        .subscribe-button-wrapper {
            margin: 24px 0;
            text-align: center;
        }
    }

    .modal-content {
        display: flex;
        justify-content: space-between;

        @media (min-width: breakpoints.$sm-breakpoint) {
            padding: 48px !important;
        }

        @media (min-width: breakpoints.$md-breakpoint) {
            justify-content: flex-start;
        }

        @media (max-width: breakpoints.$md-breakpoint-max-width) {
            padding: 0 16px 16px 16px;
        }

        .close-button-wrapper {
            text-align: left;
        }

        .subscribe-form-wrapper {
            width: 100%;
            text-align: center;

            .content {
                margin-top: 16px;

                .form-group {
                    text-align: left;
                }
            }

            .subscribe-image-wrapper {
                display: flex;
                width: 100%;
                align-items: center;
                justify-content: center;
            }

            .subscribe-title-wrapper {
                display: block;

                @media (min-width: breakpoints.$sm-breakpoint) {
                    display: flex;
                    justify-content: center;
                }
            }
        }
    }

    .modal-subtitle {
        margin-top: 8px;
        color: variables.$gray300;
        font-size: 14px;
        font-weight: 500;
        text-align: center;
    }

    h2 {
        color: variables.$baseWhite;
        font-family: fonts.$font-quicksand;
        font-size: 28px;
        font-weight: 700;
    }

    .error-message {
        margin-top: 2px;
        color: variables.$error600;
        font-size: 12px;
        line-height: 19px;
    }

    .locked-image img {
        width: 70px;
    }

    .privacy-policy {
        color: legacyVariables.$body-color;
        font-size: 11px;

        a {
            color: legacyVariables.$body-color;
            text-decoration: underline;

            &:hover {
                opacity: 0.9;
            }
        }
    }

    label {
        color: variables.$gray300;
        font-size: 12px;
    }

    .input {
        width: 100%;
        height: 44px;
        padding: 6px 12px;
        border: 1px solid legacyVariables.$twilight-purple;
        border-radius: 10px;
        background: transparent;
        color: variables.$baseWhite;
        font-size: 16px;
        outline: none;
        transition: 0.2s ease-in-out;

        &::placeholder {
            color: legacyVariables.$twilight-purple;
        }
    }

    .error-wrapper--active {
        border: 1px solid variables.$error600;
    }

    .subscribe-image {
        max-width: 80px;
    }

    @media (max-width: breakpoints.$md-breakpoint-max-width) {
        .subscribe-image {
            max-width: 110px;
        }

        .modal-content {
            border: 0;

            button {
                width: 100%;
                margin-right: 3px;
                margin-bottom: 4px;
            }
        }
    }
}
</style>
